// frontend/src/components/JournalEditor/JournalEditor.js
/* global chrome */
import React, { useState, useEffect, useContext, useRef } from "react";
import { TemplateContext } from "../../context/TemplateContext";
import InstructionPopup from "../InstructionPopup/InstructionPopup";
import AdjustmentPreviewModal from "../AdjustmentPreviewModal/AdjustmentPreviewModal";
import TopBar from "../TopBar/TopBar";
import DocumentPicker from "../DocumentPicker/DocumentPicker";
import DocumentTab from "../DocumentTab/DocumentTab";
import "./JournalEditor.css";
import { UserContext } from "../../UserContext";
import apiClient from "../../apiClient";
import { toast } from 'react-toastify';

// Komponenter
import JournalTab from "./components/JournalTab";
import BottomBar from "./components/BottomBar";
import TranscriptionTab from "./components/TranscriptionTab";
import DocumentsTab from "./components/DocumentsTab";
import RegeneratePopup from "./components/Popups/RegeneratePopup";
import TemplateChangePopup from "./components/Popups/TemplateChangePopup";
import SignPdfPopup from "./components/Popups/SignPdfPopup";
import ImageGallery from "../ImageGallery/ImageGallery";
import SessionNameField from "../SessionNameField/SessionNameField";
import PatientSelector from "../PatientSelector/PatientSelector";
// Journal Improvement Components
import AmendmentSection from "./components/AmendmentSection";
import AttachmentsPanel from "./components/AttachmentsPanel";
import AuditLogModal from "./components/AuditLogModal";
import PreviewModal from "./components/PreviewModal";
import JournalSearchModal from "./components/JournalSearchModal";
import {
  applyOrderFromTemplate,
  extractSectionContent
} from "./utils/sectionHelpers";
import { useJournalSections } from "./hooks/useJournalSections";
import { useDocumentGeneration } from "./hooks/useDocumentGeneration";
import { useJournalTransfer } from "./hooks/useJournalTransfer";
import { useSaveToPatient } from "./hooks/useSaveToPatient";


/**
 * Tillåter ENBART rubriker som finns i den aktiva journalmallen.
 * Allt annat (“felrubriker”) läggs som text i föregående sektion.
 *
 *  – allowedTitles  = mängden rubriker från mallen (småbokstäver, trimmade)
 *  – looksTooLong   = skydd mot att extremt långa rader råkar tolkas som rubrik
 */


/* ========================================================================== */
const JournalEditor = ({
  sessionId,
  inputText = "",
  selectedTemplate = null,
  templates = [],
  streamingSections = [],
  generatedSections = [],
  isTranscribing = false,
  onAdjust,
  onRegenerate,
  onChangeTemplate,
  onUpdateGeneratedSections,
  startDateTime,
  sessionName = '',
  onSessionNameChange,
  // Nya props för patientjournal-integration
  patientId = null,
  selectedPatient: initialSelectedPatient = null,
  onPatientChange,
  appointmentId = null,
  useInternalJournal = false,
  onJournalSaved,
  transcriptionUpdatedAfterJournal = false,
}) => {
  /* ---------- Custom Hooks ---------- */
  const {
    sections,
    setSections,
    error,
    setError,
    hasPartial,
    hasFinal,
    handleSectionChange,
    handleDeleteSection,
    addSection,
    getJournalText,
    setDisableAutosaveUntil,
    saveStatus,
    lastSavedAt,
    markSaved
  } = useJournalSections({
    selectedTemplate,
    streamingSections,
    generatedSections,
    isTranscribing,
    sessionId
  });

  const {
    openDocs,
    setOpenDocs,
    generateDocument: handleGenerateDocument,
    handleDocSectionChange
  } = useDocumentGeneration(sessionId);


  const {
    handleTransferClick,
    handleTransferDocument,
    selectedJournalSystem,
    showSignPopup,
    hasCertificate,
    signError,
    isSigningLoading,
    handleSignPdf,
    handleSkipSign,
    handleCancelSign
  } = useJournalTransfer(sections, selectedTemplate, sessionId);

  const {
    saveToPatient,
    saveAndSign,
    isSaving,
    isSigning,
    savedEntryId
  } = useSaveToPatient(sections, selectedTemplate, sessionId);

  /* ---------- UI-state ---------- */
  const [showInstructionPopup, setShowInstructionPopup] = useState(false);
  const [showRegeneratePopup, setShowRegeneratePopup] = useState(false);
  const [showTemplateChangePopup, setShowTemplateChangePopup] = useState(false);
  const [pendingTemplateId, setPendingTemplateId] = useState(null);

  // Adjustment preview states
  const [showAdjustmentPreview, setShowAdjustmentPreview] = useState(false);
  const [originalSections, setOriginalSections] = useState([]);
  const [adjustedSections, setAdjustedSections] = useState([]);
  const [isAdjusting, setIsAdjusting] = useState(false);

  // Image gallery state
  const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false);

  // Journal improvement modals state
  const [showAuditLogModal, setShowAuditLogModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [journalPreferences, setJournalPreferences] = useState({
    show_preview_before_sign: true,
    enable_quick_phrases: true
  });

  // Patient state - kan överskrivas lokalt om man väljer patient i editorn
  const [selectedPatient, setSelectedPatient] = useState(initialSelectedPatient);
  const currentPatientId = selectedPatient?.patient_id || patientId;

  // Synka med initial patient om den ändras utifrån
  useEffect(() => {
    if (initialSelectedPatient && !selectedPatient) {
      setSelectedPatient(initialSelectedPatient);
    }
  }, [initialSelectedPatient]);

  /* ---------- Flik-hantering ---------- */
  const [activeSubTab, setActiveSubTab] = useState("journal");

  /* ---------- Användarkontext ---------- */
  const { user } = useContext(UserContext);

  const { setTemplateId } = useContext(TemplateContext);

  /* ---------- Datum/tid ---------- */
  const displayDate = startDateTime ? startDateTime.toLocaleString('sv-SE') : "";

  /* ---------- Keyboard shortcuts ---------- */
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only trigger if not in an input/textarea (unless Escape)
      const isEditing = ['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName);

      // Ctrl+S - Save journal
      if (e.ctrlKey && e.key === 's' && !e.shiftKey) {
        e.preventDefault();
        if (currentPatientId && useInternalJournal && !isSaving && !isSigning) {
          saveToPatient(currentPatientId, appointmentId).then((entryId) => {
            if (entryId && onJournalSaved) {
              onJournalSaved(entryId);
            }
          });
        }
      }

      // Ctrl+Shift+S - Sign journal
      if (e.ctrlKey && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        if (currentPatientId && useInternalJournal && !isSaving && !isSigning) {
          handleSignWithPreview();
        }
      }

      // Ctrl+F - Open search (when not editing)
      if (e.ctrlKey && e.key === 'f' && !isEditing) {
        e.preventDefault();
        setShowSearchModal(true);
      }

      // Escape - Close modals
      if (e.key === 'Escape') {
        if (showSearchModal) setShowSearchModal(false);
        if (showPreviewModal) setShowPreviewModal(false);
        if (showAuditLogModal) setShowAuditLogModal(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    currentPatientId, useInternalJournal, isSaving, isSigning,
    showSearchModal, showPreviewModal, showAuditLogModal,
    saveToPatient, appointmentId, onJournalSaved
  ]);

  /* ---------- Varna vid stängning med osparade ändringar ---------- */
  const hasUnsavedChanges = saveStatus === 'unsaved' || saveStatus === 'saving';
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (e) => {
      // Webbläsaren visar sin egen standardtext
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);


  /* ---------- Patient selection handlers ---------- */
  // Patientvalet lever enbart i frontend-state — inspelningssessionen (Notes)
  // känner inte till patienten. Kopplingen sparas i patient_journal_entries
  // när journalen sparas till patient.
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);

    // Meddela förälder om patientändring
    if (onPatientChange) {
      onPatientChange(patient);
    }

    toast.success(`Patient ${patient.firstname} ${patient.lastname} vald`);
  };

  const handlePatientClear = () => {
    setSelectedPatient(null);

    // Meddela förälder om patientändring
    if (onPatientChange) {
      onPatientChange(null);
    }
  };

  /* ---------- Image Gallery handlers ---------- */
  const handleOpenImageGallery = () => {
    setIsImageGalleryOpen(true);
  };

  const handleCloseImageGallery = () => {
    setIsImageGalleryOpen(false);
  };

  /* ---------- Sign with preview handlers ---------- */
  const handleSignWithPreview = () => {
    if (journalPreferences.show_preview_before_sign) {
      setShowPreviewModal(true);
    } else {
      // Direct sign without preview
      saveAndSign(currentPatientId, savedEntryId, appointmentId).then((success) => {
        if (success && onJournalSaved) {
          onJournalSaved(savedEntryId);
        }
      });
    }
  };

  const handleConfirmSign = () => {
    setShowPreviewModal(false);
    saveAndSign(currentPatientId, savedEntryId, appointmentId).then((success) => {
      if (success && onJournalSaved) {
        onJournalSaved(savedEntryId);
      }
    });
  };

  /* ---------- Search handlers ---------- */
  const handleSearchSelect = (entry) => {
    setShowSearchModal(false);
    // Navigate to the selected journal entry
    // This could trigger a callback to parent or navigate
    console.log('Selected entry:', entry.entry_id);
    toast.info(`Vald journal: ${entry.patient_name}`);
  };

  /* ---------- Copy-funktion (behålls separat) ---------- */
  const handleCopyContent = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success('Texten är kopierad till urklipp!');
      })
      .catch((e) => {
        console.error("Clipboard-fel:", e);
        toast.error("Kunde inte kopiera text.");
      });
  };

  const confirmTemplateChange = () => {
    setShowTemplateChangePopup(false);
    pendingTemplateId && onChangeTemplate?.(pendingTemplateId);
  };

  /* ---------- Justering / regenerering ---------- */
  const handleJusteraClick = () => setShowInstructionPopup(true);
  
  const handleInstructionSubmit = async (instruction) => {
    setShowInstructionPopup(false);
    
    if (!instruction.trim()) {
      setError("Instruktion kan inte vara tom.");
      return;
    }

    if (sections.length === 0) {
      setError('Inga sektioner att justera.');
      return;
    }

    try {
      // Show loading message
      setIsAdjusting(true);
      
      // Store original sections before making adjustment
      setOriginalSections([...sections]);
      
      const resp = await apiClient.post('/api/journal/adjust', {
        instruction,
        sections: sections.map((sec) => ({
          title: sec.title,
          content: sec.content,
        })),
      });
      
      if (resp.data.sections && resp.data.sections.length > 0) {
        // Set adjusted sections and show preview modal
        setAdjustedSections(resp.data.sections);
        setIsAdjusting(false);
        setShowAdjustmentPreview(true);
      } else {
        setIsAdjusting(false);
        setError('Justering returnerade inga sektioner.');
      }
    } catch (err) {
      setIsAdjusting(false);
      setError('Fel vid justering av journal: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleAcceptAdjustments = async (finalSections) => {
    setShowAdjustmentPreview(false);
    
    // Disable autosave for 5 seconds to prevent conflict with manual save
    setDisableAutosaveUntil(Date.now() + 5000);
    
    // Update generatedSections in parent component FIRST to prevent conflicts
    if (onUpdateGeneratedSections) {
      onUpdateGeneratedSections(finalSections);
    }
    
    // Then update local sections
    setSections(finalSections);
    
    // Save to backend
    const combinedText = finalSections
      .map((s) => `${s.title}\n${s.content.trim()}`)
      .join('\n\n');
    
    if (sessionId) {
      try {
        await apiClient.patch(`/recorder/sessions/${sessionId}/update_journal`, {
          generated_journal: combinedText,
        });
        markSaved();
        console.log('Successfully saved to backend');
      } catch (err) {
        console.error('Kunde inte spara justerad journal:', err);
        setError('Kunde inte spara justerad journal.');
      }
    }
  };

  const handleCancelAdjustments = () => {
    setShowAdjustmentPreview(false);
    setOriginalSections([]);
    setAdjustedSections([]);
  };

  const handleRegenerateClick = () => setShowRegeneratePopup(true);
  const handleRegenerateConfirm = () => {
    setShowRegeneratePopup(false);
    onRegenerate?.();
  };

  /* ========== Kommenterade bort - nu använder vi hooks istället ========== */

  /* ---------- Saknade sektioner-dropdown ---------- */
  const [missingSections, setMissingSections] = useState([]);
  useEffect(() => {
    if (!selectedTemplate) return setMissingSections([]);
    const used = new Set(sections.map((s) => s.title.trim().toLowerCase()));
    const miss = (selectedTemplate.sections || [])
      .filter(
        (tmpl) => !used.has(tmpl.section_title.trim().toLowerCase())
      )
      .sort((a, b) => a.order - b.order);
    setMissingSections(miss);
  }, [selectedTemplate, sections]);


  const tabs = [
      "journal",
      "documents",
      ...openDocs.map((d) => `doc-${d.docId}`),
      "transcription",
    ];

  /* ========== UI-rendering ========== */
  return (
      <div className="journal-editor-container">
        {/* ---------- topbar ---------- */}
        <TopBar
          showTemplateDropdown={false}    // <── för att dölja mall/mikrofon
          showTabs={true}                          /* <── använd const tabs */
          tabs={tabs}
          activeTab={activeSubTab}
          onTabChange={setActiveSubTab}
          docTabs={openDocs}                /* för att TopBar ska kunna slå upp titlar */
        />

        {/* ---------- innehåll ---------- */}
        <div className="journal-editor-content">
          {/* === Varning: ny transkription sedan journal genererades === */}
          {transcriptionUpdatedAfterJournal && activeSubTab === "journal" && hasFinal && (
            <div className="transcription-updated-warning">
              <span>Ny transkription har inkommit sedan journalen genererades.</span>
              <button onClick={handleRegenerateClick} className="regenerate-warning-btn">
                Generera om journalen
              </button>
            </div>
          )}

          {/* === Autosave-indikator === */}
          {activeSubTab === "journal" && hasFinal && saveStatus !== 'idle' && (
            <div className={`autosave-indicator autosave-indicator--${saveStatus}`}>
              {saveStatus === 'saving' && (
                <><i className="fas fa-spinner fa-spin"></i> Sparar…</>
              )}
              {saveStatus === 'saved' && lastSavedAt && (
                <><i className="fas fa-check"></i> Sparad {lastSavedAt.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}</>
              )}
              {saveStatus === 'unsaved' && (
                <><i className="fas fa-pen"></i> Osparade ändringar</>
              )}
            </div>
          )}

          {/* === Journal === */}
          {activeSubTab === "journal" && (
            <JournalTab
              sections={sections}
              selectedTemplate={selectedTemplate}
              displayDate={displayDate}
              hasPartial={hasPartial}
              hasFinal={hasFinal}
              isTranscribing={isTranscribing}
              onSectionChange={handleSectionChange}
              onDeleteSection={handleDeleteSection}
              onCopyContent={handleCopyContent}
              sessionId={sessionId}
              sessionName={sessionName}
              onSessionNameChange={onSessionNameChange}
            />
          )}

          {/* === Dokument-översikt === */}
          {activeSubTab === "documents" && (
            <DocumentsTab
              clinicId={user?.clinic_id}
              openDocs={openDocs}
              onGenerateDocument={(template) => handleGenerateDocument(template, getJournalText(), setActiveSubTab)}
            />
          )}

          {/* === Enskilda dokumentflikar === */}
          {activeSubTab.startsWith("doc-") &&
            openDocs
              .filter((d) => `doc-${d.docId}` === activeSubTab)
              .map((doc) => {
                // Visa transferknappen endast för Rehabiliteringsplan + TakeCare
                const isRehabPlan =
                  doc.title.trim().toLowerCase() === "rehabiliteringsplan";
                const showTransfer =
                  selectedJournalSystem === "TakeCare" && isRehabPlan;

                return (
                  <DocumentTab
                    key={doc.docId}
                    doc={doc}
                    onChangeSection={handleDocSectionChange}
                    handleCopyContent={handleCopyContent}
                    showTransferButton={showTransfer}
                    onTransferClick={handleTransferDocument}
                  />
                );
              })}


          {/* === Transkribering === */}
          {activeSubTab === "transcription" && (
            <TranscriptionTab inputText={inputText} />
          )}

          {/* === popup/notifications === */}
          {showInstructionPopup && (
            <InstructionPopup
              onClose={() => setShowInstructionPopup(false)}
              onSubmit={handleInstructionSubmit}
            />
          )}

          <RegeneratePopup
            show={showRegeneratePopup}
            onCancel={() => setShowRegeneratePopup(false)}
            onConfirm={handleRegenerateConfirm}
          />

          <TemplateChangePopup
            show={showTemplateChangePopup}
            onCancel={() => setShowTemplateChangePopup(false)}
            onConfirm={confirmTemplateChange}
          />

          <SignPdfPopup
            show={showSignPopup}
            hasCertificate={hasCertificate}
            onSign={handleSignPdf}
            onSkip={handleSkipSign}
            onCancel={handleCancelSign}
            isLoading={isSigningLoading}
            error={signError}
          />

          {error && <p className="error-message">{error}</p>}
          {/* lite extra luft längst ned */}
          <div style={{ height: "calc(var(--bottom-bar-height) + 20px)" }} />
        </div>

        {/* ---------- bottombar (visas bara på Journal-fliken) ---------- */}
        {hasFinal && activeSubTab === "journal" && (
          <BottomBar
            templates={templates}
            selectedTemplate={selectedTemplate}
            missingSections={missingSections}
            selectedJournalSystem={selectedJournalSystem}
            onTemplateChange={(id) => {
              setPendingTemplateId(id);
              setShowTemplateChangePopup(true);
            }}
            onAddSection={(section) => {
              let next = [...sections, {
                title: section.section_title,
                content: "",
                order: section.order
              }];
              next = applyOrderFromTemplate(next, selectedTemplate);
              setSections(next);
            }}
            onJustera={handleJusteraClick}
            onRegenerate={handleRegenerateClick}
            onTransfer={handleTransferClick}
            onImagesClick={handleOpenImageGallery}
            onSearchClick={() => setShowSearchModal(true)}
            // Props för patientjournal-integration
            patientId={currentPatientId}
            selectedPatient={selectedPatient}
            onPatientSelect={handlePatientSelect}
            onPatientClear={handlePatientClear}
            useInternalJournal={useInternalJournal}
            onSaveToPatient={() => {
              saveToPatient(currentPatientId, appointmentId).then((entryId) => {
                if (entryId && onJournalSaved) {
                  onJournalSaved(entryId);
                }
              });
            }}
            onSignJournal={handleSignWithPreview}
            isSaving={isSaving}
            isSigning={isSigning}
            savedEntryId={savedEntryId}
          />
        )}

        {/* ---------- Adjustment Preview Modal ---------- */}
        <AdjustmentPreviewModal
          originalSections={originalSections}
          adjustedSections={adjustedSections}
          onAccept={handleAcceptAdjustments}
          onCancel={handleCancelAdjustments}
          isOpen={showAdjustmentPreview}
        />

        {/* ---------- Adjustment Loading Overlay ---------- */}
        {isAdjusting && (
          <div className="adjustment-loading-overlay">
            <div className="adjustment-loading-content">
              <div className="spinner"></div>
              <h3>Aion justerar din journal</h3>
              <p>Vänta medan vi processar dina ändringar...</p>
            </div>
          </div>
        )}

        {/* ---------- Humla Image Gallery ---------- */}
        <ImageGallery
          isOpen={isImageGalleryOpen}
          onClose={handleCloseImageGallery}
          sessionId={sessionId}
          autoRefresh={false}
        />

        {/* ---------- Journal Improvement Modals ---------- */}
        {showPreviewModal && (
          <PreviewModal
            journalData={{
              template_name: selectedTemplate?.template_name
            }}
            sections={sections}
            patientInfo={{
              name: 'Patient', // This should come from parent component
              date: startDateTime
            }}
            onSign={handleConfirmSign}
            onClose={() => setShowPreviewModal(false)}
            signing={isSigning}
          />
        )}

        {showAuditLogModal && savedEntryId && (
          <AuditLogModal
            entryId={savedEntryId}
            patientName="Patient"
            onClose={() => setShowAuditLogModal(false)}
          />
        )}

        {showSearchModal && (
          <JournalSearchModal
            onClose={() => setShowSearchModal(false)}
            onSelectEntry={handleSearchSelect}
          />
        )}
      </div>
    );
};

export default JournalEditor;
